import { useEffect, useMemo, useRef, useState } from "react";
import { Input, Button, Header, IconName, Icon } from "react-frontier-v2";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import API from "../API";
import { Issue, IssuesStatus, UserTag } from "../SiteClasses";
import { ListBoardIssue, ListTableIssue } from "../components";
import { useUser } from "../hooks";
import UserAccess from "../UserAccess";

var IssuesList=()=>{
   var { user, hasAccess } = useUser();
   var navigate = useNavigate();
   var defaultTagId = user?.tags?.[0]?.tag_id || 0;
   var [searchParams, setSearchParams] = useSearchParams();

   var defaultStartDate = useMemo(() => moment().subtract(2, 'months').startOf('day').toDate(), []);

   var [tags, setTags]=useState<UserTag[]>(null);
   var [boardIssues, setBoardIssues]=useState<Issue[]>(null);
   var [tableIssues, setTableIssues]=useState<{ items: Issue[], total: number }>(null);
   var [loadError, setLoadError]=useState<string>(null);
   var [mode, setMode]=useState<'table' | 'list'>('list');
   var [search, setSearch]=useState<string>('');
   var [statusFilter, setStatusFilter]=useState<number[]>([]);
   var [page, setPage]=useState<number>(0);
   var limit = 20;
   var [priorityFilter, setPriorityFilter]=useState<number>(0);
   var [tagsFilter, setTagsFilter]=useState<number[]>([defaultTagId]);
   var [dateStart, setDateStart]=useState<Date | null>(() => {
      var ds = searchParams.get('ds');
      if (ds) {
         var parsed = moment(ds, 'YYYY-MM-DD');
         if (parsed.isValid()) {
            return parsed.startOf('day').toDate();
         }
      }
      return defaultStartDate;
   });
   var [dateEnd, setDateEnd]=useState<Date | null>(() => {
      var de = searchParams.get('de');
      if (de) {
         var parsed = moment(de, 'YYYY-MM-DD');
         if (parsed.isValid()) {
            return parsed.endOf('day').toDate();
         }
      }
      return null;
   });
   var [debouncedSearch, setDebouncedSearch]=useState<string>('');
   var initialTagSet = useRef<boolean>(Boolean(defaultTagId));

   useEffect(() => {
      if (!initialTagSet.current && user?.tags?.[0]?.tag_id) {
         setTagsFilter([user.tags[0].tag_id]);
         initialTagSet.current = true;
      }
   }, [user]);

   useEffect(() => {
      var nextParams = new URLSearchParams(searchParams);
      var changed = false;

      if (mode === 'list') {
         if (dateStart) {
            var dsStr = moment(dateStart).format('YYYY-MM-DD');
            if (nextParams.get('ds') !== dsStr) {
               nextParams.set('ds', dsStr);
               changed = true;
            }
         } else if (nextParams.has('ds')) {
            nextParams.delete('ds');
            changed = true;
         }

         if (dateEnd) {
            var deStr = moment(dateEnd).format('YYYY-MM-DD');
            if (nextParams.get('de') !== deStr) {
               nextParams.set('de', deStr);
               changed = true;
            }
         } else if (nextParams.has('de')) {
            nextParams.delete('de');
            changed = true;
         }
      } else {
         if (nextParams.has('ds')) {
            nextParams.delete('ds');
            changed = true;
         }
         if (nextParams.has('de')) {
            nextParams.delete('de');
            changed = true;
         }
      }

      if (changed) {
         setSearchParams(nextParams, { replace: true });
      }
   }, [mode, dateStart, dateEnd]);

   var getTags=()=>{
      API.getAllTags().then((res)=>{
         if(res.error) return setLoadError(res.message);
         setTags(res.data);
      }).catch(()=>{
         setLoadError('Hubo un error inesperado cargando los tags.');
      });
   }

   useEffect(()=>{
      getTags();
   },[]);

   useEffect(() => {
      var timer = setTimeout(() => {
         setDebouncedSearch(search);
      }, 300);
      return () => clearTimeout(timer);
   }, [search]);

   useEffect(() => {
      if(mode==="list"){      
         var cancel = false;

         var dateStartUnix = dateStart ? Math.floor(dateStart.getTime() / 1000) : undefined;
         var dateEndUnix = dateEnd ? Math.floor(dateEnd.getTime() / 1000) : undefined;
         API.getAllIssues({
            priority: priorityFilter || undefined,
            tags_ids: tagsFilter.length > 0 ? tagsFilter : undefined,
            search: debouncedSearch.trim() || undefined,
            date_start: dateStartUnix,
            date_end: dateEndUnix,
            status_ids: statusFilter.length > 0 ? statusFilter : undefined,
         }).then((res)=>{
            if (cancel) return;
            if (res.error) return setLoadError(res.message);
            setBoardIssues(res.data.items);
         }).catch(()=>{
            if (cancel) return;
            setLoadError('Hubo un error inesperado cargando los tickets.');
         });

         return () => {
            cancel = true;
         };
      }
   }, [priorityFilter, tagsFilter, debouncedSearch, dateStart, dateEnd, statusFilter]);

   useEffect(() => {
      if (mode === 'table') {
         API.getAllIssues({
            search: debouncedSearch.trim() || undefined,
            status_ids: statusFilter.length > 0 ? statusFilter : undefined,
            limit: limit,
            offset: page * limit,
            tags_ids: tagsFilter.length > 0 ? tagsFilter : undefined,
         }).then((res) => {
            if (res.error) return setLoadError(res.message);
            setTableIssues(res.data);
         }).catch(() => {
            setLoadError('Hubo un error inesperado cargando los tickets.');
         });
      }
   }, [mode, debouncedSearch, statusFilter, page, tagsFilter, dateStart, dateEnd, priorityFilter]);
   
   var onResetFilters = () => {
      setTagsFilter([user.tags[0].tag_id]);
      setStatusFilter([]);
      setPriorityFilter(0);
      setDateStart(defaultStartDate);
      setDateEnd(null);
   };

   var currentTagName = useMemo(() => {
      if (!tagsFilter || !tagsFilter[0]) return null;
      return tags?.find(t => t.tag_id === tagsFilter[0])?.tag_name || null;
   }, [tagsFilter, tags]);

   var currentStatusName = useMemo(() => {
      if (!statusFilter || !statusFilter[0]) return null;
      switch (statusFilter[0]) {
         case IssuesStatus.PENDING: return 'Abierto';
         case IssuesStatus.IN_PROGRESS: return 'En progreso';
         case IssuesStatus.ON_HOLD: return 'En espera';
         case IssuesStatus.RESOLVED: return 'Resuelto';
         case IssuesStatus.CANCELLED: return 'Cancelado';
         default: return null;
      }
   }, [statusFilter]);

   var currentPriorityName = useMemo(() => {
      switch (priorityFilter) {
         case 1: return 'Baja';
         case 2: return 'Media';
         case 3: return 'Alta';
         case 4: return 'Crítica';
         default: return null;
      }
   }, [priorityFilter]);
   
   if(loadError) return <Header text={"Error"} subtext={loadError} />;
   if(!tags) return <Header loading />;
   if(mode === 'list' && boardIssues === null) return <Header loading />;
   if(mode === 'table' && tableIssues === null) return <Header loading />;

   return <main>
      <div style={{ padding: '8px 15px', background: '#ffffff', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: mode === 'table' ? 'flex-end' : 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
         {mode === 'list' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
               <Input 
                  value={search}
                  onChange={setSearch}
                  placeholder="Buscar por ID o título..."
                  iconName="magnifying-glass"
                  style={{ width: 210, marginBottom: 0 }}
                  inputStyle={{ height: 32, fontSize: 13 }}
               />
               <div style={{ width: 1, height: 20, background: '#e5e7eb', margin: '0 2px' }} />
               <Button 
                  size="small"
                  color={currentTagName ? 'black' : 'white'}
                  text={currentTagName ? `${currentTagName}` : 'Departamento'}
                  iconRight="caret-down"
                  iconRightStyle={{ marginRight: 0, marginLeft: 6 }}
                  menu={[
                     { text: 'Todos', onClick: () => setTagsFilter([0]) },
                     ...(tags || []).map((tag) => ({
                        text: tag.tag_name,
                        onClick: () => setTagsFilter([tag.tag_id])
                     }))
                  ]}
               />
               <Button 
                  size="small"
                  color={currentStatusName ? 'black' : 'white'}
                  text={currentStatusName ? `${currentStatusName}` : 'Estatus'}
                  iconRight="caret-down"
                  iconRightStyle={{ marginRight: 0, marginLeft: 6 }}
                  menu={[
                     { text: 'Todos', onClick: () => setStatusFilter([0]) },
                     { text: 'Abierto', onClick: () => setStatusFilter([IssuesStatus.PENDING]) },
                     { text: 'En progreso', onClick: () => setStatusFilter([IssuesStatus.IN_PROGRESS]) },
                     { text: 'En espera', onClick: () => setStatusFilter([IssuesStatus.ON_HOLD]) },
                     { text: 'Resuelto', onClick: () => setStatusFilter([IssuesStatus.RESOLVED]) },
                     { text: 'Cancelado', onClick: () => setStatusFilter([IssuesStatus.CANCELLED]) },
                  ]}
               />
               <Button 
                  size="small"
                  color={currentPriorityName ? 'black' : 'white'}
                  text={currentPriorityName ? `${currentPriorityName}` : 'Prioridad'}
                  iconRight="caret-down"
                  iconRightStyle={{ marginRight: 0, marginLeft: 6 }}
                  menu={[
                     { text: 'Todas', onClick: () => setPriorityFilter(0) },
                     { text: 'Baja', onClick: () => setPriorityFilter(1) },
                     { text: 'Media', onClick: () => setPriorityFilter(2) },
                     { text: 'Alta', onClick: () => setPriorityFilter(3) },
                     { text: 'Crítica', onClick: () => setPriorityFilter(4) },
                  ]}
               />
               <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '2px 8px', height: 32, boxSizing: 'border-box' }}>
                  <Icon name="calendar" style={{ color: '#6b7280', fontSize: 13, marginRight: 2 }} />
                  <Input 
                     placeholder="Inicio" 
                     calendar={{
                        date: dateStart || undefined,
                        mode: 'date',
                        format: 'dd/MM/yyyy',
                        onSelected: (d: Date) => setDateStart(moment(d).startOf('day').toDate())
                     }}
                     style={{ width: 85, marginBottom: 0 }}
                     inputStyle={{ border: 'none', background: 'transparent', padding: '0 4px', fontSize: 13, height: 26, boxShadow: 'none' }}
                  />
                  <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>
                  <Input 
                     placeholder="Fin" 
                     calendar={{
                        date: dateEnd || undefined,
                        mode: 'date',
                        format: 'dd/MM/yyyy',
                        onSelected: (d: Date) => setDateEnd(moment(d).endOf('day').toDate())
                     }}
                     style={{ width: 85, marginBottom: 0 }}
                     inputStyle={{ border: 'none', background: 'transparent', padding: '0 4px', fontSize: 13, height: 26, boxShadow: 'none' }}
                  />
               </div>
               <Button 
                  size="small"
                  color="white"
                  text="Reiniciar filtros"
                  iconName="rotate-left"
                  onClick={onResetFilters}
               />
            </div>
         )}
         <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
               <span style={{ fontSize: 13, fontWeight: 600, color: '#4b5563' }}>Vista:</span>
               <Button size="small" onClick={()=>{ setMode('list') }} icon iconName="table" color={mode==='list' ? 'black' : 'white'} />
               <Button size="small" onClick={()=>{ setMode('table') }} icon iconName="list-1-2" color={mode==='table' ? 'black' : 'white'} />
            </div>
            {mode === 'list' && hasAccess(UserAccess.ISSUES.VIEW_ISSUE_TYPES) && (
               <Button 
                  text="Opciones" 
                  color="black" 
                  iconRight="caret-down" 
                  iconRightStyle={{ marginRight: 0, marginLeft: 5 }} 
                  size="small"
                  menu={[
                     {
                        text: 'Editar tipos',
                        iconName: 'list' as IconName,
                        onClick: ()=>navigate('/issues/types')
                     }
                  ]}
               />
            )}
         </div>
      </div>
      
      <div style={{ display: "flex", flexDirection: mode==='list' ? 'row' : 'column', padding: 10, gap: mode==='list' ? 10 : 0, width: '100%' }}>
         {mode==='list' ? (
            <ListBoardIssue issues={boardIssues || []} />
         ) : (
            <ListTableIssue 
               tags={tags || []}
               issues={tableIssues?.items || []} 
               search={search}
               onSearchChange={setSearch}
               total={tableIssues?.total || 0}
               page={page}
               limit={limit}
               onPageChange={setPage}
               selectedStatuses={statusFilter}
               onStatusChange={setStatusFilter}
               onTagsChange={setTagsFilter}
               selectedTags={tagsFilter}
            />
         )}
      </div>
   </main>
}

export default IssuesList;
